/**
 * Chat Panel (desktop right-side)
 *
 * Fixed-width (380px) panel that occupies the right edge of the desktop
 * app shell. Replaces the former ChatInput bottom drawer.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader2, Trash2, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import { useChatSyncContext } from '../contexts/ChatSyncContext';
import { MarkdownContent, StreamingMarkdownContent } from './MarkdownContent';
import { VoiceButton } from './VoiceButton';
import type { ChatPageContext } from '../api/client';

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  context?: ChatPageContext;
  onResponse?: () => void;
}

export function ChatPanel({ isOpen, onClose, context, onResponse }: ChatPanelProps) {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isLoading,
    streamingContent,
    toolStatus,
    sendMessageStream,
    clearMessages,
    openPopOut,
    setChatContext,
  } = useChatSyncContext();

  useEffect(() => {
    setChatContext(context);
  }, [context, setChatContext]);

  // Allow external callers (e.g. the Getting Started checklist) to preload
  // the chat input with a starter prompt — useful when a button has a clear
  // "try this" example and we want one click instead of "open chat, then
  // type the example yourself". Same-tab CustomEvent, no global store needed.
  useEffect(() => {
    const onSet = (e: Event) => {
      const next = (e as CustomEvent<string>).detail;
      if (typeof next === 'string') {
        setInput(next);
        // Focus after the input update so the cursor lands at the end.
        requestAnimationFrame(() => {
          const ta = textareaRef.current;
          if (ta) {
            ta.focus();
            ta.setSelectionRange(next.length, next.length);
          }
        });
      }
    };
    window.addEventListener('danny:chat-input:set', onSet);
    return () => window.removeEventListener('danny:chat-input:set', onSet);
  }, []);

  // Auto-resize textarea using rAF — min 3 rows, expands up to ~40% viewport height
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const frameId = requestAnimationFrame(() => {
      textarea.style.height = 'auto';
      const maxH = Math.max(240, window.innerHeight * 0.4);
      textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, 72), maxH)}px`;
    });
    return () => cancelAnimationFrame(frameId);
  }, [input]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (chatContainerRef.current && isOpen) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isOpen, streamingContent]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!input.trim() || isLoading) return;
      const message = input.trim();
      setInput('');
      const response = await sendMessageStream(message, context);
      if (response?.success && onResponse) {
        onResponse();
      }
    },
    [input, isLoading, sendMessageStream, context, onResponse],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!isOpen) return null;

  const hasMessages = messages.length > 0;

  return (
    <aside
      data-testid="chat-panel"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
      className={clsx(
        'hidden md:flex flex-col shrink-0 w-[460px] h-full relative border-l border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light dark:bg-surface-base-dark',
      )}
      aria-label="Chat with Danny"
    >
      {/* Header — floats over scrolling messages with progressive blur */}
      <div className="absolute top-0 inset-x-0 z-10 pointer-events-none">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-surface-base-light via-surface-base-light/90 to-transparent dark:from-surface-base-dark dark:via-surface-base-dark/90 dark:to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 backdrop-blur-md"
          style={{
            maskImage:
              'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 42%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0) 100%)',
            WebkitMaskImage:
              'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 42%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0) 100%)',
          }}
        />
      <header className="relative z-10 pointer-events-auto flex items-center justify-between px-4 pt-2 pb-6">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={openPopOut}
            disabled={isLoading}
            className={clsx(
              'p-1 rounded transition-colors',
              isLoading
                ? 'text-text-tertiary-light/40 dark:text-text-tertiary-dark/40 cursor-not-allowed'
                : 'text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark',
            )}
            title={isLoading ? 'Wait for response to finish' : 'Open in new window'}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            className="p-1 rounded text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </header>
      </div>

      {/* Messages — stacked; user messages are bubbles, assistant fills full width */}
      <div
        ref={chatContainerRef}
        className="absolute inset-0 overflow-y-auto p-3 pt-14 pb-44 space-y-3"
      >
        {messages.map((msg) =>
          msg.role === 'user' ? (
            <div key={msg.id} className="flex justify-end py-2">
              <div className="max-w-full rounded-lg px-3 py-2 text-sm bg-primary-700 text-white">
                <p className="whitespace-pre-wrap">{msg.content}</p>
                {msg.actions && msg.actions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-white/20 space-y-1">
                    {msg.actions.map((action, i) => (
                      <p key={i} className="text-xs opacity-75">✓ {action.description}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              key={msg.id}
              className="w-full rounded-lg border border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light/50 dark:bg-surface-hover-dark/50 px-3 py-2.5 text-sm text-text-primary-light dark:text-text-primary-dark"
            >
              <MarkdownContent content={msg.content} />
              {msg.actions && msg.actions.length > 0 && (
                <div className="mt-2 pt-2 border-t border-border-subtle-light/40 dark:border-border-subtle-dark/40 space-y-1">
                  {msg.actions.map((action, i) => (
                    <p key={i} className="text-xs opacity-75">✓ {action.description}</p>
                  ))}
                </div>
              )}
            </div>
          ),
        )}

        {isLoading && (
          <div className="w-full rounded-lg border border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light/50 dark:bg-surface-hover-dark/50 px-3 py-2.5 text-sm text-text-primary-light dark:text-text-primary-dark">
            {toolStatus ? (
              <div className="flex items-center gap-2 text-text-secondary-light dark:text-text-secondary-dark">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{toolStatus}</span>
              </div>
            ) : streamingContent ? (
              <StreamingMarkdownContent content={streamingContent} />
            ) : (
              <Loader2 className="w-4 h-4 animate-spin text-text-secondary-light dark:text-text-secondary-dark" />
            )}
          </div>
        )}
      </div>

      {/* Input bar — taller, no left icon, voice + send on the right */}
      <div className="absolute bottom-0 inset-x-0 z-10 pointer-events-none">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-surface-base-light via-surface-base-light/90 to-transparent dark:from-surface-base-dark dark:via-surface-base-dark/90 dark:to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 backdrop-blur-md"
          style={{
            maskImage:
              'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 42%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0) 100%)',
            WebkitMaskImage:
              'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 42%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0) 100%)',
          }}
        />
      <form onSubmit={handleSubmit} className="relative z-10 pointer-events-auto p-3 pt-8">
        <div className={clsx(
          'flex flex-col min-w-0 bg-surface-base-light/95 dark:bg-surface-base-dark/95 backdrop-blur-md rounded-2xl border shadow-lg transition-all',
          'border-border-subtle-light dark:border-border-subtle-dark focus-within:border-primary-300 dark:focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100 dark:focus-within:ring-primary-900/50',
        )}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Danny anything... (Shift+Enter for new line)"
            rows={3}
            className="chat-input w-full min-w-0 px-3 pt-2.5 pb-1 bg-transparent resize-none focus:outline-none text-sm text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark"
            style={{ minHeight: '72px' }}
            disabled={isLoading}
          />

          <div className="flex items-center justify-between gap-0.5 px-1.5 pb-1.5">
            {hasMessages && (
              <button
                type="button"
                onClick={clearMessages}
                className="p-2 rounded-lg text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
                title="Clear conversation"
                aria-label="Clear conversation"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <div className="flex items-center gap-0.5">
            <VoiceButton onTranscription={(text) => setInput(text)} disabled={isLoading} />

            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className={clsx(
                'flex-shrink-0 p-2 rounded-lg transition-colors',
                input.trim() && !isLoading
                  ? 'text-primary-500 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30'
                  : 'text-text-tertiary-light dark:text-text-tertiary-dark',
              )}
              aria-label="Send message"
            >
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
            </div>
          </div>
        </div>
      </form>
      </div>
    </aside>
  );
}
